import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';

interface Membership {
  plan: string;
  status: string;
  active: boolean;
  interval: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

const planName = (key: string) =>
  key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Free';

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

const MembershipStatus: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [m, setM] = useState<Membership | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/billing/membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setM(await res.json());
    } catch {
      setError('Could not load membership.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const checkout = new URLSearchParams(window.location.search).get('checkout');

  useEffect(() => {
    load();
    // Returning from Stripe Checkout — the webhook updates membership async, so
    // refetch a moment later to pick up the new subscription.
    if (checkout === 'success') {
      const t = setTimeout(load, 3000);
      return () => clearTimeout(t);
    }
  }, [load, checkout]);

  const manage = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/billing/portal-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setError(data.error || 'Could not open the billing portal.');
    } catch {
      setError('Could not open the billing portal.');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel your membership? You keep access until the end of the paid period.')) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/billing/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setM(await res.json());
    } catch {
      setError('Could not cancel right now. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const banner = (
    <>
      {checkout === 'success' && (
        <div className="alert alert-success">
          Payment received — your membership will update shortly.
        </div>
      )}
      {checkout === 'cancel' && <div className="alert alert-warning">Checkout was cancelled.</div>}
    </>
  );

  if (loading) {
    return (
      <>
        {banner}
        <Card body className="mb-4 text-body-tertiary">Loading your membership…</Card>
      </>
    );
  }
  if (!m) return banner;

  const expired = !m.active && m.status !== 'none';

  return (
    <>
      {banner}
      <Card className="mb-4">
      <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          {m.active ? (
            <>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5 className="mb-0 text-body-emphasis">{planName(m.plan)} plan</h5>
                <Badge bg="success">Active</Badge>
              </div>
              {m.cancel_at_period_end && m.current_period_end ? (
                <div className="text-warning-emphasis">
                  Your membership ends on {fmtDate(m.current_period_end)} and won&apos;t renew.
                </div>
              ) : m.current_period_end ? (
                <div className="text-body-tertiary">Renews on {fmtDate(m.current_period_end)}.</div>
              ) : (
                <div className="text-body-tertiary">Your membership is active.</div>
              )}
            </>
          ) : expired ? (
            <>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5 className="mb-0 text-body-emphasis">{planName(m.plan)} plan</h5>
                <Badge bg="danger">Expired</Badge>
              </div>
              <div className="text-body-tertiary">
                Your membership has {m.status === 'canceled' ? 'been cancelled' : 'expired'}
                {m.current_period_end ? ` on ${fmtDate(m.current_period_end)}` : ''}. Choose a plan
                below to reactivate.
              </div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5 className="mb-0 text-body-emphasis">Free plan</h5>
              </div>
              <div className="text-body-tertiary">
                Upgrade to unlock more features — choose a plan below.
              </div>
            </>
          )}
          {error && <div className="text-danger mt-2 small">{error}</div>}
        </div>

        {m.active && (
          <div className="d-flex gap-2 flex-shrink-0">
            <Button variant="dark" disabled={busy} onClick={manage}>
              Manage subscription
            </Button>
            {!m.cancel_at_period_end && (
              <Button variant="outline-danger" disabled={busy} onClick={cancel}>
                {busy ? 'Working…' : 'Cancel'}
              </Button>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
    </>
  );
};

export default MembershipStatus;
